// One day of a patient's medication administration: medicines down the side,
// the ward's medication rounds across the top, one cell per dose.
//
// The screen this replaces fetched every dose ever scheduled for the admission
// with no date bound — months of history and weeks of future in a single flat
// scroll, medication mixed with dressings. A day at a time is the whole point.
//
// Round columns are derived from the doses themselves (their time_slot_label,
// ordered by the clock time they actually fall at) rather than hard-coded, so a
// ward that runs different rounds gets its own columns. Doses that carry no
// round at all — the interval frequencies, Q6H and friends, which are generated
// by clock arithmetic — cannot sit in a column and would silently vanish, so
// they get their own list below the grid.
import React, { useMemo } from "react";
import {
  Box, Button, Chip, IconButton, Paper, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import { NURSE_COLORS } from "../theme";
import { todayStr, shiftDay } from "../dateNav";
import { formatIstTime, formatWallClockTime, wallClockDayKey } from "../../../utils/istDateTime";
import { outcomeOf, isGiveable, drugLabel, doseRouteLabel, isMedicationTask, sourceLabel } from "../medicationDisplay";

// The two columns of this grid carry different kinds of time. A dose's
// scheduled_datetime is the slot it was booked into — 08:00 means the 08:00
// round — so it is read verbatim. completed_at is the instant the nurse gave
// it, which arrives in UTC, so it has to be converted before it is shown.
const dayOf = wallClockDayKey;
const timeOf = formatWallClockTime;

// One-off and on-demand doses are not ward rounds — a STAT or PRN dose has no
// recurring slot, so making it a column would invent a round that doesn't exist.
const NON_ROUND_LABELS = /^(stat|prn|extra|sos)$/i;
const isRoundDose = (t) => Boolean(t.time_slot_label) && !NON_ROUND_LABELS.test(t.time_slot_label);

function DoseCell({ task, onGive }) {
  if (!task) return <TableCell sx={{ color: "text.disabled" }}>—</TableCell>;
  const st = outcomeOf(task);
  return (
    <TableCell>
      {isGiveable(task) ? (
        <Button
          size="small"
          variant={st.key === "due" ? "contained" : "outlined"}
          color={st.key === "due" ? "warning" : "inherit"}
          onClick={() => onGive(task)}
        >
          {st.key === "due" ? "Give" : timeOf(task.scheduled_datetime)}
        </Button>
      ) : (
        <Stack spacing={0.25}>
          <Chip size="small" label={st.label} color={st.color} variant={st.variant} />
          {task.completed_at && (
            <Typography variant="caption" color="text.secondary">{formatIstTime(task.completed_at)}</Typography>
          )}
          {sourceLabel(task) && (
            <Typography variant="caption" color="text.secondary">from {sourceLabel(task)}</Typography>
          )}
        </Stack>
      )}
    </TableCell>
  );
}

export default function MarGrid({ tasks, date, onDateChange, onGive, nurseNameById = {} }) {
  const meds = useMemo(() => tasks.filter(isMedicationTask), [tasks]);

  // Columns come from every dose we hold, not just the selected day, so the
  // grid keeps the same shape as the nurse steps between days.
  const rounds = useMemo(() => {
    const byLabel = new Map();
    meds.filter(isRoundDose).forEach((t) => {
      const at = timeOf(t.scheduled_datetime);
      const seen = byLabel.get(t.time_slot_label);
      if (!seen || at < seen) byLabel.set(t.time_slot_label, at);
    });
    return Array.from(byLabel.entries())
      .sort((a, b) => String(a[1]).localeCompare(String(b[1])))
      .map(([label, at]) => ({ label, at }));
  }, [meds]);

  const onDay = useMemo(() => meds.filter((t) => dayOf(t.scheduled_datetime) === date), [meds, date]);

  // A medicine is a row. Doses of the same order share a prescription; older
  // care-plan doses have none, so fall back to the drug's name.
  const rows = useMemo(() => {
    const byMedicine = new Map();
    onDay.filter(isRoundDose).forEach((t) => {
      const key = t.prescription_id != null ? `rx:${t.prescription_id}` : `name:${drugLabel(t)}`;
      if (!byMedicine.has(key)) byMedicine.set(key, { key, sample: t, bySlot: {} });
      byMedicine.get(key).bySlot[t.time_slot_label] = t;
    });
    return Array.from(byMedicine.values());
  }, [onDay]);

  const offRound = useMemo(
    () => onDay.filter((t) => !isRoundDose(t))
      .sort((a, b) => String(a.scheduled_datetime).localeCompare(String(b.scheduled_datetime))),
    [onDay]
  );

  const isToday = date === todayStr();

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 1.5, flexWrap: "wrap" }} useFlexGap>
        <Typography variant="subtitle1" sx={{ flex: 1 }}>Administration Record</Typography>
        <IconButton size="small" onClick={() => onDateChange(shiftDay(date, -1))} aria-label="Previous day">
          <ChevronLeftIcon />
        </IconButton>
        <TextField
          size="small" type="date" value={date}
          onChange={(e) => e.target.value && onDateChange(e.target.value)}
          sx={{ width: 165 }}
        />
        <IconButton size="small" onClick={() => onDateChange(shiftDay(date, 1))} aria-label="Next day">
          <ChevronRightIcon />
        </IconButton>
        {!isToday && <Button size="small" onClick={() => onDateChange(todayStr())}>Today</Button>}
      </Stack>

      <Paper variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: NURSE_COLORS.pageBg }}>
                <TableCell sx={{ minWidth: 220 }}>Medicine</TableCell>
                {rounds.map((r) => (
                  <TableCell key={r.label} sx={{ whiteSpace: "nowrap" }}>
                    {r.label}
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>{r.at}</Typography>
                  </TableCell>
                ))}
                {rounds.length === 0 && <TableCell>Doses</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.key} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{drugLabel(row.sample)}</Typography>
                    <Typography variant="caption" color="text.secondary">{doseRouteLabel(row.sample)}</Typography>
                  </TableCell>
                  {rounds.map((r) => (
                    <DoseCell key={r.label} task={row.bySlot[r.label]} onGive={onGive} />
                  ))}
                  {rounds.length === 0 && <TableCell>—</TableCell>}
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={Math.max(rounds.length + 1, 2)} align="center" sx={{ py: 3, color: "text.secondary" }}>
                    No rounds scheduled on this day.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Paper>

      {offRound.length > 0 && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Timed &amp; one-off doses
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              hourly cycles, STAT and as-needed doses — no fixed round
            </Typography>
          </Typography>
          <Paper variant="outlined" sx={{ borderRadius: 2 }}>
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: NURSE_COLORS.pageBg }}>
                    <TableCell>Time</TableCell><TableCell>Medicine</TableCell><TableCell>Dose / Route</TableCell><TableCell>Status</TableCell><TableCell>Nurse</TableCell><TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {offRound.map((t) => {
                    const st = outcomeOf(t);
                    return (
                      <TableRow key={t.id} hover>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {t.completed_at ? formatIstTime(t.completed_at) : timeOf(t.scheduled_datetime)}
                          {t.time_slot_label && <Chip size="small" label={t.time_slot_label} sx={{ ml: 0.5 }} />}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{drugLabel(t)}</TableCell>
                        <TableCell>{doseRouteLabel(t)}</TableCell>
                        <TableCell><Chip size="small" label={st.label} color={st.color} variant={st.variant} /></TableCell>
                        <TableCell>{nurseNameById[t.performed_by_id] || "—"}</TableCell>
                        <TableCell align="right">
                          {isGiveable(t) && <Button size="small" variant="contained" onClick={() => onGive(t)}>Give</Button>}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </Paper>
        </Box>
      )}
    </Box>
  );
}
